import * as React from "react"
import { useTranslation, Trans } from "react-i18next"
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Icon,
  MediaPreview,
  cn,
  getAvatarColor,
} from "@nexus/ui"
import { formatMetricValue } from "@/features/dashboard/lib/format"
import { useTenantCurrency } from "@/lib/use-tenant-currency"
import {
  personaInitials,
  humanizePersonaIndustry,
  humanizePersonaTitle,
  humanizePersonaSentence,
} from "../../lib/persona-format"
import type { PersonaOnboardingReviewItem } from "../../types/personas.types"
import { AcceptedBar } from "./AcceptedBar"
import { AdExamplesRail } from "./AdExamplesRail"
import { IndustryMetricGrid } from "./IndustryMetricGrid"
import { IndustryPendingActions } from "./IndustryPendingActions"
import { MetricTile } from "./MetricTile"
import { PeekActions } from "./PeekActions"
import { PendingActions } from "./PendingActions"
import { RefiningBar } from "./RefiningBar"
import { RejectedBar } from "./RejectedBar"
import { StatusPill } from "./StatusPill"
import type { PersonasOnboardingCardVariant } from "./persona-onboarding-card.types"

/** Fixed dark-on-pastel text — matches `UserAvatar` so hashed avatar colors stay legible. */
const AVATAR_FALLBACK_TEXT_COLOR = "hsl(0, 0%, 25%)"
const CARD_EASE = "cubic-bezier(0.32, 0.72, 0, 1)"

export interface PersonasOnboardingCardProps {
  item: PersonaOnboardingReviewItem
  /** Share of pool for this suggestion (0–100). Brand: spend share; industry: market presence. */
  sharePct: number | null
  /** Brand = spend/duplicates KPIs + suggest; industry = market KPIs + reject/add only. */
  variant?: PersonasOnboardingCardVariant
  isFocused?: boolean
  isBusy?: boolean
  onAccept: () => void
  onReject: () => void
  /** Undo the current decision (Accept or Reject) — reset to Suggested in place (no carousel advance). */
  onUndo?: () => void
  /** Click peek cards to bring them into focus. */
  onFocus?: () => void
  /** Primary Continue / Finish for the focused card only (AD-2531). */
  primaryAction?: {
    label: string
    onClick: () => void
    disabled?: boolean
    isLoading?: boolean
  }
}

/** Brand / industry review card — matches Persona Onboarding Figma layout. */
export function PersonasOnboardingCard({
  item,
  sharePct,
  variant = "brand",
  isFocused = true,
  isBusy = false,
  onAccept,
  onReject,
  onUndo,
  onFocus,
  primaryAction,
}: PersonasOnboardingCardProps) {
  const { t } = useTranslation()
  const { symbol: currencySymbol } = useTenantCurrency()
  const { persona, decisionStatus, rejectionNote } = item
  const isIndustry = variant === "industry"
  const displayTitle = humanizePersonaTitle(persona.title)
  const displayPain = persona.pains[0] ? humanizePersonaSentence(persona.pains[0]) : null
  const displayWhy = persona.motivations[0]
    ? humanizePersonaSentence(persona.motivations[0])
    : null
  const initials = personaInitials(displayTitle)
  const isAccepted = decisionStatus === "ACCEPTED"
  const isRejected = decisionStatus === "REJECTED"
  const isPending = decisionStatus === "PENDING"
  const spend = persona.spend

  return (
    <article
      data-testid="personas-onboarding-card"
      data-variant={variant}
      data-focused={isFocused ? "true" : "false"}
      aria-hidden={!isFocused}
      onClick={!isFocused && onFocus ? onFocus : undefined}
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-[14px] border bg-card",
        "transition-[border-color,box-shadow] duration-300",
        // Figma: active border is always primary (never flips on accept/reject).
        isFocused
          ? isIndustry
            ? "border-[var(--competitor)] shadow-[0_12px_44px_rgba(147,51,234,0.1)]"
            : "border-primary shadow-[0_12px_44px_rgba(11,0,243,0.1)]"
          : "cursor-pointer border-border shadow-[0_4px_18px_rgba(0,0,0,0.05)]",
      )}
      style={{ transitionTimingFunction: CARD_EASE }}
    >
      {/* Figma shell: single padded column — header / body / actions share 22×26 inset + 13px gap. */}
      <div className="flex h-full min-h-0 flex-col gap-[13px] px-[26px] py-[22px]">
        <header className="flex items-start gap-3">
          {persona.avatarUrl ? (
            <div
              className="group/avatar size-[42px] shrink-0"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <span className="relative block size-full transition-transform duration-200 ease-in-out motion-safe:group-hover/avatar:scale-110">
                <MediaPreview
                  src={persona.avatarUrl}
                  thumbnailSrc={persona.avatarUrl}
                  type="image"
                  alt={displayTitle}
                  title={displayTitle}
                  className="size-full overflow-hidden rounded-full ring-1 ring-border transition-shadow duration-200 ease-in-out group-hover/avatar:ring-2 group-hover/avatar:ring-primary/50 motion-reduce:transition-none"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-foreground/50 opacity-0 transition-opacity duration-200 ease-in-out group-hover/avatar:opacity-100 motion-reduce:transition-none"
                >
                  <Icon name="maximize-2" size="xs" className="text-background" />
                </span>
              </span>
            </div>
          ) : (
            <Avatar className="size-[42px] shrink-0 text-sm font-semibold">
              <AvatarFallback
                style={{
                  backgroundColor: getAvatarColor(persona.personalityKey),
                  color: AVATAR_FALLBACK_TEXT_COLOR,
                }}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[19px] font-semibold leading-[1.2] tracking-[-0.01em] text-foreground">
                {displayTitle}
              </h3>
              {isIndustry ? (
                <span className="inline-flex items-center rounded-sm bg-[var(--violet-subtle)] px-1.5 py-0.5 text-[10px] font-medium leading-none text-[var(--violet-subtle-foreground)]">
                  {t("personas.onboarding.industry.marketBadge", "MARKET")}
                </span>
              ) : null}
            </div>
            {persona.industry ? (
              <Badge
                variant="slate"
                className={cn(
                  "mt-1",
                  isIndustry &&
                    "border-transparent bg-[var(--violet-subtle)] text-[var(--violet-subtle-foreground)]",
                )}
              >
                {humanizePersonaIndustry(persona.industry)}
              </Badge>
            ) : null}
          </div>
          <StatusPill
            status={decisionStatus}
            variant={variant}
            hasNote={Boolean(rejectionNote)}
          />
        </header>

        <div className="flex min-h-0 flex-1 gap-5">
          <div className="flex min-w-0 flex-1 flex-col gap-[11px]">
            {isIndustry ? (
              <IndustryMetricGrid persona={persona} sharePct={sharePct} />
            ) : (
              <div className="grid shrink-0 grid-cols-2 gap-[7px]">
                <MetricTile
                  value={
                    spend > 0
                      ? formatMetricValue(spend, "currency", currencySymbol)
                      : t("personas.onboarding.review.metricEmpty", "—")
                  }
                  label={
                    sharePct != null
                      ? t("personas.onboarding.review.spendShareLabel", {
                          defaultValue: "share of spend · {{share}}%",
                          share: Math.round(sharePct),
                        })
                      : t("personas.onboarding.review.spendLabel", "tracked spend")
                  }
                />
                <MetricTile
                  value={
                    persona.adCount > 0
                      ? String(persona.adCount)
                      : t("personas.onboarding.review.metricEmpty", "—")
                  }
                  label={t("personas.onboarding.review.adsLabel", "ads")}
                />
                <MetricTile
                  value={t("personas.onboarding.review.metricPending", "—")}
                  label={t("personas.onboarding.review.duplicatesLabel", "duplicates")}
                  muted
                />
                <MetricTile
                  value={
                    persona.avgDays != null
                      ? t("personas.onboarding.review.avgDaysValue", {
                          defaultValue: "{{days}}d",
                          days: Math.round(persona.avgDays),
                        })
                      : t("personas.onboarding.review.metricEmpty", "—")
                  }
                  label={t("personas.onboarding.review.avgDaysLabel", "avg days / ad")}
                  muted={persona.avgDays == null}
                />
              </div>
            )}

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
              {persona.who ? (
                <p className="text-[13px] leading-relaxed text-foreground">
                  <Trans
                    i18nKey="personas.onboarding.review.whoLine"
                    defaults="<bold>Who:</bold> {{who}}"
                    values={{ who: persona.who }}
                    components={{ bold: <span className="font-bold" /> }}
                  />
                </p>
              ) : (
                <p className="text-[13px] italic text-muted-foreground">
                  {t(
                    "personas.onboarding.review.noDescription",
                    "No description available for this persona yet.",
                  )}
                </p>
              )}
              {displayPain ? (
                <p className="text-[13px] leading-relaxed text-foreground">
                  <Trans
                    i18nKey="personas.onboarding.review.painLine"
                    defaults="<bold>Pain:</bold> {{pain}}"
                    values={{ pain: displayPain }}
                    components={{ bold: <span className="font-bold" /> }}
                  />
                </p>
              ) : null}
              {displayWhy ? (
                <p className="text-[13px] leading-relaxed text-foreground">
                  <Trans
                    i18nKey="personas.onboarding.review.whyLine"
                    defaults="<bold>Why:</bold> {{why}}"
                    values={{ why: displayWhy }}
                    components={{ bold: <span className="font-bold" /> }}
                  />
                </p>
              ) : null}
            </div>
          </div>

          <AdExamplesRail isFocused={isFocused} title={displayTitle} topAds={persona.topAds} />
        </div>

        <div
          className={cn(
            "shrink-0 border-t pt-3",
            isIndustry ? "border-[var(--competitor)]/20" : "border-border/60",
            // Peek: status/actions are decorative — card shell handles focus.
            !isFocused && "pointer-events-none",
          )}
        >
          {isRejected && rejectionNote ? (
            <RefiningBar
              note={rejectionNote}
              isBusy={isBusy}
              onCancelEdit={isFocused ? onUndo : undefined}
            />
          ) : isRejected ? (
            <RejectedBar
              // Reconsider only when focused — peek shows the status strip alone.
              onUndo={isFocused ? onUndo : undefined}
              isBusy={isBusy}
              variant={variant}
            />
          ) : isAccepted ? (
            <AcceptedBar
              onUndo={isFocused ? onUndo : undefined}
              isBusy={isBusy}
              variant={variant}
            />
          ) : !isFocused ? (
            <PeekActions variant={variant} />
          ) : isIndustry ? (
            <IndustryPendingActions
              isBusy={isBusy}
              disabled={!isPending || isBusy}
              onReject={onReject}
              onAccept={onAccept}
            />
          ) : (
            <PendingActions
              isBusy={isBusy}
              disabled={!isPending || isBusy}
              onReject={onReject}
              onAccept={onAccept}
            />
          )}
          {isFocused && primaryAction ? (
            <div className="mt-2.5">
              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled || primaryAction.isLoading}
              >
                {primaryAction.label}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  )
}
